import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getCandidateProfileById,
  getCandidateSkills,
  getCandidateProjects,
  getCandidateEducation,
  getCandidateExperiences,
  getCandidateCertifications,
  getCandidateAwards,
  patchProfile,
} from "../api/candidateProfile.api";
import type { PaginationRequest } from "@/interface/common";
import type { ProfilePayload } from "@/interface/profile";
import { candidateProfileKeys, candidateKeys, STALE_5_MIN } from "@/lib/queryKey";
import { getNextPage, isPaginated } from "@/utils/helper";

export { candidateProfileKeys };

export {
  useUpdateProfile,
  useProfileCompletion,
} from "@/pages/profile/employee/sections/hooks/useProfile";

export const usePatchCandidateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<ProfilePayload> | FormData;
    }) => patchProfile(id, payload),
    onSuccess: (_data, variables) => {
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: candidateProfileKeys.detail(variables.id),
        });
      }
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["exportPreview"] });
    },
  });
};

export { usePatchCandidateProfile as usePatchProfile };

export {
  useCreateProfileSkill,
  useUpdateProfileSkill,
  usePatchProfileSkill,
  useDeleteProfileSkill,
  useDeleteProfileSkillCategory,
} from "@/pages/profile/employee/sections/hooks/useProfileSkills";

export {
  useCreateProject,
  useUpdateProject,
  usePatchProject,
  useDeleteProject,
} from "@/pages/profile/employee/sections/hooks/useProjects";

export {
  useCreateEducation,
  useUpdateEducation,
  usePatchEducation,
  useDeleteEducation,
} from "@/pages/profile/employee/sections/hooks/useEducation";

export {
  useCreateExperience,
  useUpdateExperience,
  usePatchExperience,
  useDeleteExperience,
} from "@/pages/profile/employee/sections/hooks/useExperience";

export {
  useCreateCertification,
  useUpdateCertification,
  usePatchCertification,
  useDeleteCertification,
} from "@/pages/profile/employee/sections/hooks/useCertifications";

export {
  useCreateAward,
  useUpdateAward,
  usePatchAward,
  useDeleteAward,
} from "@/pages/profile/employee/sections/hooks/useAwards";

export const useCandidateProfileById = (
  id?: number | string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: candidateProfileKeys.detail(id || ""),
    queryFn: () => getCandidateProfileById(id!),
    enabled: Boolean(id) && enabled,
    ...STALE_5_MIN,
  });
};

export const useCandidateSkills = (
  profileId?: number | string,
  params?: PaginationRequest,
) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: [...candidateProfileKeys.skills(profileId), params],
    queryFn: ({ pageParam }) =>
      getCandidateSkills(
        profileId!,
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    enabled: Boolean(profileId),
  });
};

export const useCandidateProjects = (
  profileId?: number | string,
  params?: PaginationRequest,
) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: [...candidateProfileKeys.projects(profileId), params],
    queryFn: ({ pageParam }) =>
      getCandidateProjects(
        profileId!,
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    enabled: Boolean(profileId),
  });
};

export const useCandidateEducation = (
  profileId?: number | string,
  params?: PaginationRequest,
) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: [...candidateProfileKeys.education(profileId), params],
    queryFn: ({ pageParam }) =>
      getCandidateEducation(
        profileId!,
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    enabled: Boolean(profileId),
  });
};

export const useCandidateExperiences = (
  profileId?: number | string,
  params?: PaginationRequest,
) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: [...candidateProfileKeys.experiences(profileId), params],
    queryFn: ({ pageParam }) =>
      getCandidateExperiences(
        profileId!,
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    enabled: Boolean(profileId),
  });
};

export const useCandidateCertifications = (
  profileId?: number | string,
  params?: PaginationRequest,
) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: [...candidateProfileKeys.certifications(profileId), params],
    queryFn: ({ pageParam }) =>
      getCandidateCertifications(
        profileId!,
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    enabled: Boolean(profileId),
  });
};

export const useCandidateAwards = (
  profileId?: number | string,
  params?: PaginationRequest,
) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: [...candidateProfileKeys.awards(profileId), params],
    queryFn: ({ pageParam }) =>
      getCandidateAwards(
        profileId!,
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    enabled: Boolean(profileId),
  });
};
